import { LabeledInput, LabeledSelect } from "@/Components/Form";
import React, { useState } from "react";
import { Inertia } from "@inertiajs/inertia";
import { Formheader } from "@/Components";
import { DollarSign, PieChart, Settings } from "react-feather";
import { toast } from "react-toastify";
import { usePage } from "@inertiajs/inertia-react";

export default function SettingsDetails({ organization, errors, focus }) {
  let [dirty, setDirty] = useState(false);

  const industries = usePage().props?.industries || [];

  let [values, setValues] = useState({
    name: organization.data.name || "",
    email: organization.data.email || "",
    phone: organization.data.phone || "",
    organization_number: organization.data.organization_number || "",
    industry: organization.data.industry || "",
    employees: organization.data.employees || "",
    yearly_revenue: organization.data.yearly_revenue || "",
    consolidation_approach:
      organization.data.consolidation_approach || "equity_share",
  });

  function isSaveDisabled() {
    return (
      !(
        values.name &&
        values.email &&
        values.phone &&
        values.organization_number
      ) || !dirty
    );
  }

  function handleUpdate(event) {
    setDirty(true);
    setValues((prevValues) => {
      return { ...prevValues, [event.target.name]: event.target.value };
    });
  }

  function handleSave(e) {
    e.preventDefault();

    if (isSaveDisabled()) {
      return;
    }

    const id = toast.loading("Please wait...");

    Inertia.put(`/company-settings/${organization.data.id}`, values, {
      onSuccess: () => {
        toast.update(id, {
          render: "The organization details has been updated",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
        });
        setDirty(false);
      },
      onError: () => {
        toast.update(id, {
          render: "An error occurred while updating the organization",
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
        });
      },
    });
  }

  function SettingsSaveButton() {
    let disabled = false;

    if (isSaveDisabled()) {
      disabled = true;
    }

    return (
      <button
        disabled={disabled}
        className="btn-blue ml-auto max-w-fit self-end"
      >
        Save
      </button>
    );
  }

  const consolidationApproach = [
    {
      value: "equity_share",
      label: "Equity share",
      description:
        "Teams accounts for emissions according to share of equity in the operation",
      icon: (
        <PieChart className="size-10 rounded-full bg-gray p-2 text-gray-inactive peer-checked:bg-primary peer-checked:text-white" />
      ),
    },
    {
      value: "financial_control",
      label: "Financial Control",
      description:
        "Teams account for emissions from operations over which it has financial control",
      icon: (
        <DollarSign className="size-10 rounded-full bg-gray p-2 text-gray-inactive peer-checked:bg-primary peer-checked:text-white" />
      ),
    },
    {
      value: "operational_control",
      label: "Operational Control",
      description:
        "Teams account for emissions from operations over which it has operational control",
      icon: (
        <Settings className="size-10 rounded-full bg-gray p-2 text-gray-inactive peer-checked:bg-primary peer-checked:text-white" />
      ),
    },
  ];

  return (
    <div className="shadow-small mb-6 bg-gray-light p-8">
      <form onSubmit={handleSave} key={organization.data.id}>
        <div className="flex flex-row flex-wrap justify-between">
          <div className="mb-4 w-full">
            <LabeledInput
              title="Organization number*"
              placeholder="Enter organization number"
              name="organization_number"
              required="required"
              value={values.organization_number}
              onChange={handleUpdate}
              error={errors.organization_number}
            />
          </div>

          <div className="mb-4 w-full">
            <LabeledInput
              title="Name of the organization*"
              placeholder="Enter name of organization"
              name="name"
              required="required"
              value={values.name}
              onChange={handleUpdate}
              error={errors.name}
            />
          </div>

          <div className="mb-4 w-full">
            <LabeledSelect
              title="Industry"
              name="industry"
              options={industries}
              value={values?.industry || ""}
              defaultOption="Select industry"
              className="mb-4"
              focus={focus === "industry"}
              onChange={handleUpdate}
              error={errors.industry}
            />
          </div>

          <div className="mb-4 w-full">
            <LabeledInput
              title="Number of employees"
              placeholder="Enter number of employees"
              name="employees"
              value={values.employees}
              onChange={handleUpdate}
              error={errors.employees}
              focus={focus === "industry"}
            />
          </div>

          <div className="mb-4 w-full">
            <LabeledInput
              title="Yearly revenue (tkr)"
              placeholder="Enter yearly revenue in tkr"
              name="yearly_revenue"
              value={values.yearly_revenue}
              onChange={handleUpdate}
              error={errors.yearly_revenue}
              focus={focus === "industry"}
            />
          </div>

          <div className="mb-4 w-full">
            <LabeledInput
              title="Contact e-mail*"
              placeholder="Enter contact e-mail"
              name="email"
              required="required"
              value={values.email}
              onChange={handleUpdate}
              error={errors.email}
            />
          </div>

          <div className="mb-4 w-full">
            <LabeledInput
              title="Contact phone number*"
              placeholder="Enter contact phone number"
              name="phone"
              required="required"
              value={values.phone}
              onChange={handleUpdate}
              error={errors.phone}
            />
          </div>

          <div className="w-full">
            <Formheader>Consolidation approach</Formheader>
            <p>
              The consolidation approach for emissions from teams reflects the
              teams level of control and, consequently, the extent to which the
              teams emissions are consolidated into your total emissions
              footprint.
            </p>

            <div className="mb-4 mt-4 flex w-full flex-col gap-4 md:flex-row">
              {consolidationApproach.map((approach) => (
                <label
                  htmlFor={`consolidation_approach_${approach.value}`}
                  key={approach.value}
                  className="shadow-small grow cursor-pointer border border-borderColor p-8 has-[:checked]:border-primary has-[:checked]:bg-primary-light"
                >
                  <div className="flex flex-row-reverse justify-between">
                    <input
                      type="radio"
                      name="consolidation_approach"
                      id={`consolidation_approach_${approach.value}`}
                      value={approach.value}
                      className="peer"
                      onChange={handleUpdate}
                      checked={values.consolidation_approach === approach.value}
                    />
                    {approach.icon}
                  </div>
                  <Formheader>{approach.label}</Formheader>
                  <p>{approach.description}</p>
                </label>
              ))}
            </div>
          </div>

          <SettingsSaveButton />
        </div>
      </form>
    </div>
  );
}
